import { useCallback, useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import ResearchService from '../../../services/ResearchService';
import TradeService from '../../../services/TradeService';
import { COUNTRY } from '../../constants/country';
import { RESEARCH } from '../../constants/queryDefinition';
import { RetailPlatforms } from '../../constants/retail';
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter';
import useAnalytics, { getVinBasicProperties } from '../useAnalytics';
import SegmentConfig from '../../constants/segmentConfig';

function getSaleDateFrom(dateRange: number) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - dateRange);
  const pastDate = new Date(date).toISOString().slice(0, 10);
  return pastDate;
}

function isValidTrim(trim: string | undefined) {
  if (!trim) {
    return false;
  }

  const formattedTrim = trim
    .replace(/\s+w\//i, '')
    .replace(/r\/t/i, '')
    .replace(/t\/a/i, '');

  if (formattedTrim.includes('/')) {
    return false;
  }
  return true;
}

interface IUseResearch {
  average: number;
  highest: number;
  lowest: number;
  decodedTrim?: string;
  decodedMake?: string;
  decodedModel?: string;
  decodedYear?: number;
}

const useResearch = (
  isPricingAnalysisComponentEnabled: boolean,
  tradeDetails: any
) => {
  const [isResearchDataTracked, setIsResearchDataTracked] = useState(false);
  const researchService = useMemo(() => ResearchService.get(), []);
  const tradeService = useMemo(() => TradeService.get(), []);
  const track = useAnalytics();

  const { vin, trim, model, modelYear, manufacturerId } = {
    vin: tradeDetails?.vin,
    trim: tradeDetails?.trim,
    model: tradeDetails?.vehicleSummary?.model?.displayName,
    modelYear: tradeDetails?.vehicleSummary?.modelYear,
    manufacturerId: tradeDetails?.vehicleSummary?.model?.manufacturerId
  };

  const getResearchPrices = useCallback<
    () => Promise<IUseResearch>
  >(async () => {
    const researchPricesResponse = await researchService.get90DaysPrices({
      manufacturerId,
      model,
      modelYear,
      trim
    });

    const { average, max, min } = researchPricesResponse;

    return { average, highest: max, lowest: min, decodedTrim: trim };
  }, [researchService, manufacturerId, model, modelYear, trim]);

  const getPriceAnalysis = useCallback<
    () => Promise<IUseResearch>
  >(async () => {
    let decodedBestTrimName;
    let decodedYear;
    let decodedMake;
    let decodedModel;
    try {
      const decodedVinResponse = await tradeService.decodeVin({ vin });
      ({
        decodedBestTrimName,
        decodedYear,
        decodedMake,
        decodedModel
      } = decodedVinResponse);

      if (!isValidTrim(decodedBestTrimName)) {
        decodedBestTrimName = '';
        throw new Error('trim is not valid');
      }

      const saleDateTo = getSaleDateFrom(0);
      const saleDateFrom = getSaleDateFrom(90);
      const countryCode = COUNTRY.CA;

      const getPricingAnalysis = await researchService.getPricingAnalysis({
        year: decodedYear,
        modelName: decodedModel,
        trim: decodedBestTrimName,
        saleDateTo,
        saleDateFrom,
        countryCode
      });

      let {
        priceAggAve,
        priceAggMax,
        priceAggMin,
        // eslint-disable-next-line prefer-const
        totalNumResults
      } = getPricingAnalysis;

      if (totalNumResults === 0) {
        priceAggAve = 0;
        priceAggMax = 0;
        priceAggMin = 0;
      }

      return {
        average: priceAggAve,
        highest: priceAggMax,
        lowest: priceAggMin,
        decodedTrim: decodedBestTrimName,
        decodedMake,
        decodedModel,
        decodedYear
      };
    } catch (e) {
      return {
        average: 0,
        highest: 0,
        lowest: 0,
        decodedTrim: decodedBestTrimName,
        decodedMake,
        decodedModel,
        decodedYear
      };
    }
  }, [researchService, tradeService, vin]);

  const { data: researchPrices, isFetching, isPlaceholderData } = useQuery(
    [RESEARCH.RESEARCHDETAILS, vin],
    isPricingAnalysisComponentEnabled ? getPriceAnalysis : getResearchPrices,
    {
      enabled: !!(manufacturerId && model),
      placeholderData: {
        average: 0,
        highest: 0,
        lowest: 0,
        decodedTrim: '',
        decodedMake: '',
        decodedModel: '',
        decodedYear: 0
      }
    }
  );

  const isLoaded = !isFetching && !isPlaceholderData;

  // Track "Market Guide Price Analysis Results Displayed" event
  useEffect(() => {
    if (isLoaded && !isResearchDataTracked) {
      track(SegmentConfig.names.marketGuidePriceAnalysisResultsDisplayed, {
        ...getVinBasicProperties(tradeDetails),
        platform_value: capitalizeFirstLetter(RetailPlatforms.Web),
        market_guide_price_avg_value: researchPrices?.average
          ? researchPrices.average.toString()
          : 'N/A',
        market_guide_price_max_value: researchPrices?.highest
          ? researchPrices.highest.toString()
          : 'N/A',
        market_guide_price_min_value: researchPrices?.lowest
          ? researchPrices.lowest.toString()
          : 'N/A'
      });
      setIsResearchDataTracked(true);
    }
  }, [isLoaded, researchPrices, track, tradeDetails, isResearchDataTracked]);

  return { researchPrices, isLoaded };
};

export default useResearch;
